import React from 'react';
import PropTypes from 'prop-types';
import { compose, prop } from 'ramda';
import {
  withState,
  withHandlers,
  lifecycle,
  setPropTypes,
  defaultProps,
} from 'recompose';
import { FormattedMessage } from 'react-intl';

import connect from 'app/libs/connect';
import {
  IN_QUEUE,
  ADD_TO_QUEUE,
  REMOVE_FROM_QUEUE,
} from 'app/features/watchlist/constants';
import S from 'app/features/series/components/QueueButton/QueueButton.styled';
import {
  addToWatchlist,
  removeFromWatchlist,
} from 'app/features/watchlist/actions';
import { isSeriesInWatchlist } from 'app/features/series/selectors';
import ActionLink from 'app/components/Links/ActionLink.styled';
import CallToAction from 'app/components/layout/CallToAction.styled';

const UI = ({
  isInQueue,
  isHovered,
  handleClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const queueStateId = isInQueue ? IN_QUEUE : ADD_TO_QUEUE;
  const buttonTextId = isHovered ? REMOVE_FROM_QUEUE : queueStateId;
  const queueIcon = (
    <S.Icon
      viewBox="0 0 24 24"
      data-t="queue-filled-svg"
      aria-labelledby="queue-filled-svg"
      role="img"
    >
      <title id="queue-filled-svg">
        <FormattedMessage id={buttonTextId} />
      </title>
      <path d="M18 2H6a1 1 0 00-1 1v17.06c0 .2.07.4.19.58a1 1 0 001.4.23l4.83-3.46a1 1 0 011.16 0l4.84 3.46a1 1 0 001.58-.81V3a1 1 0 00-1-1z" />
    </S.Icon>
  );

  return (
    <ActionLink
      type="orange"
      data-t="add-to-queue-btn"
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      as="span"
    >
      {!isHovered && queueIcon}
      <CallToAction size="s" type="heavy" data-t="queue-btn-text">
        <FormattedMessage id={buttonTextId} />
      </CallToAction>
    </ActionLink>
  );
};

function componentDidUpdate(prevProps) {
  const { isAdded, isLoading, isInQueue } = prevProps;
  const { setIsHovered, setInQueue } = this.props;
  const isLoadingFinished = isLoading && !this.props.isLoading;
  const isAddedChanged = isAdded !== this.props.isAdded;

  if (
    (!isLoading && isAddedChanged) ||
    (isLoadingFinished && isInQueue !== this.props.isAdded)
  ) {
    setIsHovered(false);
    setInQueue(this.props.isAdded);
  }
}

const onMouseEnter =
  ({ isInQueue, isHovered, setIsHovered }) =>
  () => {
    if (isInQueue && !isHovered) {
      setIsHovered(true);
    }
  };

const onMouseLeave =
  ({ isInQueue, setIsHovered }) =>
  () => {
    if (isInQueue) {
      setIsHovered(false);
    }
  };

const handleClick =
  ({
    isInQueue,
    isActive,
    setIsHovered,
    setInQueue,
    setIsLoading,
    seriesId,
    addToWatchlist,
    removeFromWatchlist,
  }) =>
  e => {
    e.preventDefault();

    const queueAction = isInQueue ? removeFromWatchlist : addToWatchlist;

    setIsLoading(true);
    queueAction(seriesId).finally(() => setIsLoading(false));

    if (isActive) {
      setIsHovered(false);
      setInQueue(!isInQueue);
    }
  };

UI.propTypes = {
  isInQueue: PropTypes.bool.isRequired,
  isHovered: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
};

UI.displayName = 'QueueButton';

const state = { isAdded: isSeriesInWatchlist };
const actions = { addToWatchlist, removeFromWatchlist };

const QueueButton = compose(
  setPropTypes({ seriesId: PropTypes.string.isRequired }),
  connect(state, actions),
  defaultProps({ isAdded: false }),
  withState('isInQueue', 'setInQueue', prop('isAdded')),
  withState('isHovered', 'setIsHovered', false),
  withState('isLoading', 'setIsLoading', false),
  withHandlers({ handleClick, onMouseEnter, onMouseLeave }),
  lifecycle({ componentDidUpdate }),
)(UI);

export { UI, handleClick, onMouseEnter, onMouseLeave, componentDidUpdate };

export default QueueButton;
